import React, { useState, useEffect } from "react";
import { Modal, Form, Alert, Spinner } from "react-bootstrap";
import { Button } from "../ui";
import * as XLSX from "xlsx";
import { api } from "../../services";
import { useTranslation } from "react-i18next";
import CustomDropdown from "../dropdown/CustomDropdown";

const UploadFileModal = ({ show, handleClose, type, uploadAllowed = true }) => {
  const { t } = useTranslation("translation");
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState(""); // "json" or "excel"
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [stores, setStores] = useState([]);
  const [storesLoading, setStoresLoading] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState("");

  // Fetch user stores when modal opens
  useEffect(() => {
    const fetchStores = async () => {
      try {
        setStoresLoading(true);
        const token = localStorage.getItem("token");
        const response = await api.get("/stores/my", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const list = Array.isArray(response.data?.stores) ? response.data.stores : [];
        setStores(list);
      } catch (err) {
        console.error("Failed to fetch stores:", err.response?.data || err.message);
        setError(t("Failed to fetch stores."));
      } finally {
        setStoresLoading(false);
      }
    };
    if (show) {
      fetchStores();
    }
  }, [show, t]);

  // Handle file selection
  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    const fileExtension = selectedFile.name.split(".").pop().toLowerCase();

    if (fileExtension === "json") {
      setFileType("json");
      readJsonFile(selectedFile);
    } else if (fileExtension === "xlsx") {
      setFileType("excel");
      readExcelFile(selectedFile);
    } else if (fileExtension === "csv") {
      setFileType("csv");
      readCsvFile(selectedFile);
    } else {
      setError(`${t("Invalid file type. Please upload a JSON, Excel, or CSV file.")}`);
      return;
    }

    setFile(selectedFile);
  };

  // Read JSON File
  const readJsonFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const json = JSON.parse(e.target.result);
        setData(json);
        setError("");
      } catch (err) {
        setError(`${t("Invalid JSON format.")}`);
      }
    };
    reader.readAsText(file);
  };

  // Read Excel File
  const readExcelFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const workbook = XLSX.read(e.target.result, { type: "binary" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const parsedData = XLSX.utils.sheet_to_json(sheet);
      setData(parsedData);
      setError("");
    };
    reader.readAsBinaryString(file);
  };

  // Read CSV File
  const readCsvFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const workbook = XLSX.read(text, { type: "string" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const parsedData = XLSX.utils.sheet_to_json(sheet);
        setData(parsedData);
        setError("");
      } catch (err) {
        console.error("Invalid CSV format:", err);
        setError(t("Invalid CSV format."));
      }
    };
    reader.readAsText(file);
  };

  // Upload Data to API
  const handleUpload = async () => {
    if (!uploadAllowed) {
      setError(t("noPermissionToAccessThis"));
      return;
    }
    if (!data) {
      setError(`${t("No valid data to upload.")}`);
      return;
    }
    if (!selectedStoreId) {
      setError(t("Store ID is required"));
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    formData.append("storeId", selectedStoreId);

    setUploading(true);
    setError("");

    try {
      const token = localStorage.getItem("token");
      if (type === "products") {
        await api.post("/upload-products", formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
      } else {
        await api.post("categories/upload", formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
      }
      setUploading(false);
      handleClose();
    } catch (err) {
      setError(`${t("Failed to upload data.")}`);
      setUploading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{t("uploadjsonorexcelfile")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <div>
              <CustomDropdown
                options={stores}
                handleSelect={(store) => setSelectedStoreId(store?._id || "")}
                selectedOption={selectedStoreId}
                placeholder={t("Select store")}
                showLable={true}
                label={"Store"}
                className="modal-store-dropdown"
                disabled={storesLoading}
              />
            </div>
          </Form.Group>
          <Form.Group>
            <Form.Label>{t("selectfile")}</Form.Label>
            <Form.Control
              type="file"
              accept=".json, .xlsx, .csv"
              onChange={handleFileChange}
            />
          </Form.Group>
        </Form>

        {error && (
          <Alert variant="danger" className="mt-2">
            {error}
          </Alert>
        )}

        {data && (
          <div
            className="mt-3"
            style={{
              maxHeight: "200px",
              overflow: "auto",
              background: "#f4f4f4",
              padding: "10px",
            }}
          >
            <pre>{JSON.stringify(data, null, 2)}</pre>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          {t("close")}
        </Button>
        <Button
          variant="primary"
          onClick={handleUpload}
          disabled={!uploadAllowed || !data || uploading}
          title={!uploadAllowed ? t("noPermissionToAccessThis") : undefined}
        >
          {uploading ? (
            <Spinner as="span" animation="border" size="sm" className="me-2" />
          ) : null}
          {uploading ? `${t("uploading")}` : `${t("upload")}`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UploadFileModal;
